import type {AnalyticsApi} from './type';

const CALLBACK_TIMEOUT = 500;
const MAX_TIMEOUT_TIMES = 3;
let timeoutCounter = 0;
/**
 * Analytics may fail to load (for example, if blocked by an adblocker). If that happens, we still need to
 * resolve the logEvent promises, so we stablish a timeout for those cases.
 * If the log times out more than MAX_TIMEOUT_TIMES times consecutively, we stop waiting for events to be
 * sent and inmediately resolve all the promises.
 */
const createEventCallback = (done: () => void) => {
    if (timeoutCounter >= MAX_TIMEOUT_TIMES) {
        done();
        return () => {};
    }

    const tid = setTimeout(() => {
        done();
        timeoutCounter++;
    }, CALLBACK_TIMEOUT);

    return () => {
        clearTimeout(tid);
        timeoutCounter = 0;
        done();
    };
};

let isInitialized = false;

export const gtagApi: AnalyticsApi = {
    initialize(trackingId: string) {
        // make this function idempotent. And ignore it in SSR.
        if (typeof window === 'undefined' || isInitialized) {
            return Promise.resolve();
        }

        isInitialized = true;

        window.dataLayer = window.dataLayer || [];
        // WARN: don't refactor this into an arrow function with `...args` because google analytics won't work!
        window.gtag = function () {
            window.dataLayer.push(arguments);
        };
        window.gtag('js', new Date());
        // It's important to set send_page_view to false, otherwise the page_view event will be send twice
        window.gtag('config', trackingId, {send_page_view: false});

        const {head} = document;
        if (head) {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(trackingId)}`;
            head.insertBefore(script, head.lastChild);
        }
        return Promise.resolve();
    },
    logEvent(name: string, params: Record<string, unknown>) {
        return new Promise((resolve) => {
            window.gtag('event', name, {...params, event_callback: createEventCallback(resolve)});
        });
    },
    setScreenName(screenName: string, params: Record<string, unknown>) {
        return this.logEvent('page_view', {
            screenName: screenName,
            page_title: screenName,
            ...params,
        });
    },
    setUserProperty(name: string, value: string) {
        window.gtag('set', 'user_properties', {
            [name]: value,
        });
        return Promise.resolve();
    },
};
